import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Users, Bed, Waves, TreePine, Home as HomeIcon, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { villas, HASNA_SUITES, HASNA_ENTIRE } from "@/data/villas";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import BookingForm from "@/components/BookingForm";
import SEO from "@/components/SEO";

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-[color:hsl(var(--dh-terracotta))]" />
      <span className="text-sm">{text}</span>
    </div>
  );
}

function fmtMAD(n) {
  return `${n.toLocaleString("fr-FR")} MAD`;
}

// --------- Section : Grille tarifaire Hasna (3 suites + villa entière) ----------
function HasnaPricing({ t }) {
  const p = t.priceGrid;
  return (
    <section id="pricing" className="bg-white border-y border-[color:hsl(var(--dh-ocre)/0.15)]">
      <div className="dh-container py-20 md:py-28">
        {/* Suites */}
        <div className="max-w-2xl mb-10">
          <div className="dh-overline">{p.overline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">{p.suitesTitle}</h2>
          <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{p.suitesIntro}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-16">
          {HASNA_SUITES.map((s) => {
            const sameSeason = s.priceLow === s.priceHigh;
            return (
              <div
                key={s.key}
                data-testid={`hasna-suite-${s.key}`}
                className="dh-card overflow-hidden flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.photos[0]} alt={t.villa[s.nameKey]} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="dh-overline text-[color:hsl(var(--dh-terracotta))]">Villa Hasna</div>
                  <div className="font-serif text-2xl mt-2">{t.villa[s.nameKey]}</div>
                  <p className="mt-3 text-sm text-[color:hsl(var(--dh-muted))] flex-1">{t.villa[s.descKey]}</p>

                  <div className="mt-6 pt-6 border-t border-[color:hsl(var(--dh-ocre)/0.2)]">
                    {sameSeason ? (
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs uppercase tracking-widest text-[color:hsl(var(--dh-muted))]">{p.fromLabel}</span>
                        <span className="font-serif text-2xl">
                          {fmtMAD(s.priceLow)}
                          <span className="text-xs text-[color:hsl(var(--dh-muted))]">{p.pricePerNight}</span>
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-baseline">
                          <span className="text-[color:hsl(var(--dh-muted))]">{p.lowSeason}</span>
                          <span className="font-serif text-xl">{fmtMAD(s.priceLow)}<span className="text-xs text-[color:hsl(var(--dh-muted))]">{p.pricePerNight}</span></span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-[color:hsl(var(--dh-muted))]">{p.highSeason}</span>
                          <span className="font-serif text-xl text-[color:hsl(var(--dh-terracotta))]">{fmtMAD(s.priceHigh)}<span className="text-xs text-[color:hsl(var(--dh-muted))]">{p.pricePerNight}</span></span>
                        </div>
                      </div>
                    )}
                  </div>

                  <a href="#book" data-testid={`hasna-suite-book-${s.key}`} className="mt-6 inline-flex items-center justify-center gap-2 py-3 border border-[color:hsl(var(--dh-terracotta))] text-[color:hsl(var(--dh-terracotta))] hover:bg-[color:hsl(var(--dh-terracotta))] hover:text-white transition-colors text-xs uppercase tracking-widest">
                    {p.bookThisSuite} <ArrowRight className="h-3 w-3" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Privatisation entière */}
        <div className="dh-zellige p-8 md:p-12 border border-[color:hsl(var(--dh-ocre)/0.25)]">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="dh-overline text-[color:hsl(var(--dh-terracotta))]">{p.entireTitle}</div>
              <h3 className="font-serif text-3xl md:text-4xl mt-3">{p.hasnaEntireLabel}</h3>
              <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{p.entireIntro}</p>
              <div className="mt-4 text-xs text-[color:hsl(var(--dh-muted))]">· {p.minStay} · {p.touristTax}</div>
            </div>
            <div className="md:col-span-5 space-y-3">
              <div className="flex items-baseline justify-between border-b border-[color:hsl(var(--dh-ocre)/0.2)] pb-3">
                <span className="text-sm text-[color:hsl(var(--dh-muted))]">{p.lowSeason}</span>
                <span className="font-serif text-2xl">{fmtMAD(HASNA_ENTIRE.priceLow)}<span className="text-xs text-[color:hsl(var(--dh-muted))]">{p.pricePerNight}</span></span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-[color:hsl(var(--dh-muted))]">{p.highSeason}</span>
                <span className="font-serif text-2xl text-[color:hsl(var(--dh-terracotta))]">{fmtMAD(HASNA_ENTIRE.priceHigh)}<span className="text-xs text-[color:hsl(var(--dh-muted))]">{p.pricePerNight}</span></span>
              </div>
              <a href="#book" data-testid="hasna-book-entire" className="mt-4 dh-btn-primary w-full justify-center">
                {p.bookEntire} <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// --------- Teaser Séjour Famille (uniquement Villa Hasna) ----------
function FamilyTeaser({ t, image }) {
  const f = t.family;
  return (
    <section data-testid="hasna-family-teaser" className="bg-[color:hsl(var(--dh-palm))] text-[color:hsl(var(--dh-sand))]">
      <div className="dh-container py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{f.teaserOverline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">{f.teaserTitle}</h2>
          <p className="mt-6 opacity-85 leading-relaxed">{f.teaserSubtitle}</p>
          <div className="mt-8">
            <Link to="/sejour-famille" data-testid="hasna-family-cta" className="inline-flex items-center gap-2 bg-[color:hsl(var(--dh-terracotta))] text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-[color:hsl(var(--dh-terracotta-dark))] transition-colors">
              <Sparkles className="h-4 w-4" />
              {f.teaserCta}
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={image} alt={f.teaserTitle} className="h-full w-full object-cover" loading="lazy" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Villa() {
  const { slug } = useParams();
  const { t } = useLang();
  const [lightbox, setLightbox] = useState({ open: false, idx: 0 });
  // Villa Ismaël retirée du catalogue — toute route /villa/* mappe désormais Hasna.
  if (slug && slug !== "hasna") return <Navigate to="/villa/hasna" replace />;
  const villa = villas.hasna;

  const open = (idx) => setLightbox({ open: true, idx });
  const close = () => setLightbox({ open: false, idx: 0 });
  const next = () => setLightbox((s) => ({ open: true, idx: (s.idx + 1) % villa.gallery.length }));
  const prev = () => setLightbox((s) => ({ open: true, idx: (s.idx - 1 + villa.gallery.length) % villa.gallery.length }));

  const features = [
    { icon: Users, text: t.villa.hasnaCapacity },
    { icon: Bed, text: t.villa.hasnaSuites },
    { icon: Waves, text: t.villa.hasnaPool },
    { icon: TreePine, text: t.villa.hasnaGarden },
    { icon: HomeIcon, text: t.villa.hasnaMode },
  ];

  const sectionTitle = t.villa.hasnaSectionTitle;

  return (
    <>
      <SEO pageKey="hasna" image={`https://darhasnacollection.com${villa.hero}`} />
      {/* Hero */}
      <section className="relative -mt-20 h-[90svh] overflow-hidden">
        <img src={villa.hero} alt={villa.name} className="absolute inset-0 h-full w-full object-cover kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/60" />
        <div className="relative z-10 h-full dh-container flex flex-col justify-end pb-20 text-white">
          <div className="fade-up max-w-2xl">
            <div className="dh-overline text-[color:hsl(var(--dh-ocre))]">{t.collection[villa.tagKey]}</div>
            <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl mt-4">{villa.name}</h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl">{t.villa[villa.styleKey]}</p>
          </div>
        </div>
      </section>

      {/* Features + intro */}
      <section className="dh-container py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="dh-overline">{villa.name}</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              {sectionTitle}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((f, i) => (
                <Feature key={i} icon={f.icon} text={f.text} />
              ))}
            </div>
            <p className="text-[color:hsl(var(--dh-muted))] leading-relaxed">{t.collection.description}</p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" data-testid="villa-gallery" className="dh-container pb-20">
        <div className="dh-overline mb-6">{t.villa.gallery}</div>
        <div className="grid grid-cols-6 auto-rows-[160px] md:auto-rows-[220px] gap-3">
          {villa.gallery.map((src, i) => {
            const patterns = [
              "col-span-6 row-span-2",
              "col-span-3 row-span-2",
              "col-span-3 row-span-1",
              "col-span-3 row-span-1",
              "col-span-2 row-span-1",
              "col-span-2 row-span-1",
              "col-span-2 row-span-1",
              "col-span-3 row-span-2",
              "col-span-3 row-span-1",
              "col-span-3 row-span-1",
              "col-span-2 row-span-1",
              "col-span-2 row-span-1",
              "col-span-2 row-span-1",
            ];
            const spans = patterns[i % patterns.length];
            return (
              <button
                key={i}
                onClick={() => open(i)}
                data-testid={`gallery-item-${i}`}
                className={`group relative overflow-hidden ${spans}`}
              >
                <img src={src} alt="" loading={i < 3 ? "eager" : "lazy"} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </button>
            );
          })}
        </div>
      </section>

      {/* Pricing */}
      <HasnaPricing t={t} />

      {/* Séjour Famille teaser */}
      <FamilyTeaser t={t} image={villa.gallery[1]} />

      {/* Booking */}
      <section id="book" className="dh-container py-20 md:py-28">
        <div className="max-w-xl mb-12">
          <div className="dh-overline">{t.booking.overline}</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">{t.booking.title}</h2>
          <p className="mt-4 text-[color:hsl(var(--dh-muted))]">{t.booking.subtitle}</p>
        </div>
        <BookingForm />
      </section>

      {/* Lightbox */}
      <Dialog open={lightbox.open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-6xl bg-black/95 border-none p-0">
          <div className="relative">
            <img src={villa.gallery[lightbox.idx]} alt="" className="w-full h-[80vh] object-contain" />
            <button onClick={close} className="absolute top-4 end-4 h-10 w-10 grid place-items-center bg-white/10 text-white hover:bg-white/20"><X /></button>
            <button onClick={prev} className="absolute top-1/2 -translate-y-1/2 start-4 h-12 w-12 grid place-items-center bg-white/10 text-white hover:bg-white/20"><ChevronLeft /></button>
            <button onClick={next} className="absolute top-1/2 -translate-y-1/2 end-4 h-12 w-12 grid place-items-center bg-white/10 text-white hover:bg-white/20"><ChevronRight /></button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
